import { format } from 'date-fns'
import type IVacancyCard from '~/types/Storyblok/Vacancy/VacancyCard'
import fetchGql from '~/utils/fetchGql'

const indexQuery = gql`
  query VacancypageItemsQuery($startsWith: String!, $filter: JsonScalar) {
    VacancypageItems(starts_with: $startsWith, filter_query: $filter) {
      items {
        full_slug
        content {
          title
          cardDescription
          heroImage {
            filename
            alt
          }
          department
          location
          internalDate
          openPositions
        }
      }
    }
  }
`

const teamQuery = gql`
  query VacancypageItemsQuery($startsWith: String!, $slugs: String, $filter: JsonScalar) {
    VacancypageItems(starts_with: $startsWith, by_slugs: $slugs, filter_query: $filter) {
      items {
        full_slug
        content {
          title
          cardDescription
          heroImage {
            filename
            alt
          }
          department
          location
          internalDate
          openPositions
        }
      }
    }
  }
`

const internshipQuery = gql`
  query VacancypageItemsQuery($startsWith: String!, $filter: JsonScalar) {
    VacancypageItems(starts_with: $startsWith, filter_query: $filter) {
      items {
        full_slug
        content {
          title
          cardDescription
          heroImage {
            filename
            alt
          }
          department
          location
          internalDate
          openPositions
        }
      }
    }
  }
`

const query = gql`
  query VacancypageItemsQuery($startsWith: String!, $searchTerm: String, $slugs: String, $filter: JsonScalar) {
    VacancypageItems(starts_with: $startsWith, search_term: $searchTerm, by_slugs: $slugs, filter_query: $filter) {
      items {
        full_slug
        content {
          title
          cardDescription
          heroImage {
            filename
            alt
          }
          department
          location
          internalDate
          openPositions
        }
      }
    }
  }
`

const fetchData = (query: any, variables?: any) => {
  return fetchGql(query, variables).then((response: any) => {
    return parseData(response.VacancypageItems)
  })
}

const parseData = (data: any) => {
  const results: any = []

  data.items.forEach((item: any) => {
    results.push({
      full_slug: item.full_slug.replace(/^lv\//, '').replace(/^careers/, ''),
      ...item.content
    } as IVacancyCard)
  })

  return results
}

export const useFetchFilteredVacancies = () => {
  const now = format(new Date(), 'yyyy-MM-dd')

  const fetchIndexVacancies = (language: string) => {
    const variables: any = {
      startsWith: (language === 'en' ? '' : `${language}/`) + 'careers/vacancies/',
      filter: { showOnIndex: { is: true }, internalDate: { gt_date: now } }
    }

    return fetchData(indexQuery, variables).then((response) => response)
  }

  const fetchTeamVacancies = (language: string, team: string) => {
    const variables = {
      startsWith: (language === 'en' ? '' : `${language}/`) + 'careers/vacancies/',
      slugs: `careers/vacancies/${team}/*`,
      filter: { internalDate: { gt_date: now } }
    }

    return fetchData(teamQuery, variables)
  }

  const fetchInternshipVacancies = (language: string) => {
    const variables: any = {
      startsWith: (language === 'en' ? '' : `${language}/`) + 'careers/vacancies/',
      filter: { isInternship: { is: true }, internalDate: { gt_date: now } }
    }

    return fetchData(internshipQuery, variables)
  }

  const fetchFilteredVacancies = (language: string, teams?: string[], searchTerm?: string) => {
    const variables: any = {
      startsWith: (language === 'en' ? '' : `${language}/`) + 'careers/vacancies/',
      searchTerm: searchTerm ?? '',
      filter: { internalDate: { gt_date: now } }
    }

    if (teams?.length) {
      const slugs: string[] = []

      teams.forEach((team) => {
        const slug = `careers/vacancies/${team}/*`
        slugs.push(slug)
      })

      variables.slugs = slugs.join(',')
    } else {
      variables.slugs = 'careers/vacancies/*'
    }

    return fetchData(query, variables)
  }

  return { fetchIndexVacancies, fetchTeamVacancies, fetchInternshipVacancies, fetchFilteredVacancies }
}
